import { Node as ReactFlowNode } from '@xyflow/react';
import { useMemo } from 'react';

import { MarriageGroupNode } from '@/modules/familyTree/components/MarriageGroupNode';
import { PersonNode } from '@/modules/familyTree/components/PersonNode';

import { ArrowEdge } from '../../edges/ArrowEdge';
import { PhaseBandNode } from '../../nodes/PhaseBandNode/PhaseBandNode';
import { RelationshipLabelNode } from '../../nodes/RelationshipLabelNode/RelationshipLabelNode';
import { SectionLabelNode } from '../../nodes/SectionLabelNode';
import { TextNoteNode } from '../../nodes/TextNoteNode/TextNoteNode';
import { TileNode } from '../../nodes/TileNode/TileNode';
import {
  DefaultNodes,
  ExternalFlowChartProps,
  FlowChartProps,
  InternalFlowChartProps,
  Node,
} from '../../types';

const proOptions: InternalFlowChartProps['proOptions'] = {
  hideAttribution: true,
};

const defaultNodeTypes: Omit<
  InternalFlowChartProps['nodeTypes'],
  // Omit defaults because they are provided by reactflow out of the box
  NonNullable<DefaultNodes['type']>
> = {
  tile: TileNode,
  textNote: TextNoteNode,
  relationshipLabel: RelationshipLabelNode,
  sectionLabel: SectionLabelNode,
  person: PersonNode,
  marriageGroup: MarriageGroupNode,
  phaseBand: PhaseBandNode,
};

const defaultEdgeTypes: InternalFlowChartProps['edgeTypes'] = {
  arrow: ArrowEdge,
};

export function useDefaultProps<T extends ReactFlowNode = Node>({
  loading = false,
  slots,
  presentationMode = false,
  getState,
  ...otherInternalProps
}: FlowChartProps<T>): {
  internalProps: InternalFlowChartProps<T>;
  externalProps: ExternalFlowChartProps;
} {
  const nodeTypes = useMemo(
    () => ({ ...defaultNodeTypes, ...otherInternalProps.nodeTypes }),
    [otherInternalProps.nodeTypes]
  );
  const internalProps: InternalFlowChartProps<T> = {
    // Start: props that make a flow "editable". We initialize edit state to "locked" by default
    nodesConnectable: false,
    nodesDraggable: false,
    elementsSelectable: true,
    // End: props that make a flow "editable".

    // Have zoom occur only when "Meta" key is pressed (default behavior)
    // Ties into usePreventScrolling handler
    zoomOnScroll: false,
    zoomOnPinch: false,
    // End zoom / scroll handler

    proOptions,
    fitView: true,
    zoomOnDoubleClick: false,
    minZoom: 0.1,
    edgeTypes: defaultEdgeTypes,
    ...otherInternalProps,
    nodeTypes,
  };

  const externalProps: ExternalFlowChartProps = {
    loading,
    slots,
    presentationMode,
    getState,
  };

  return { internalProps, externalProps };
}
